import * as pc from "playcanvas";
import { RemotePlayer } from "./RemotePlayer";
import { CharacterFactory } from "./CharacterFactory";
import type { GameClient, RemotePlayerState } from "../net/GameClient";

/**
 * RemotePlayerManager — owns all remote player entities and wires their
 * lifecycle to GameClient events. Local player is excluded (rendered via
 * FirstPersonController instead).
 */
export class RemotePlayerManager {
  private app: pc.AppBase;
  private client: GameClient;
  private players = new Map<string, RemotePlayer>();
  /** Shared skinned-character loader (one set of GLBs for all peers +
   *  the local shadow body). */
  private characters: CharacterFactory;
  /** In-flight grenade ids seen last tick, to fire the throw anim once. */
  private seenGrenades = new Set<number>();

  constructor(app: pc.AppBase, client: GameClient, characters: CharacterFactory) {
    this.app = app;
    this.client = client;
    this.characters = characters;

    this.client.onPlayerAdd((player, sessionId) => this.onAdd(player, sessionId));
    // Unconditional per-snapshot feed (32 Hz keyframe stream) — the
    // interpolation buffer inside RemotePlayer needs EVERY snapshot, not
    // just the ones the change-dedup lets through.
    this.client.onRemoteSnapshot((player, sessionId) => this.onChange(player, sessionId));
    this.client.onPlayerRemove((_player, sessionId) => this.onRemove(sessionId));

    // Grenade-toss animation: when a peer's grenade first shows up in
    // flight, play its throw clip. Tracked by id so each throw fires once.
    this.client.onGrenadeState((msg) => {
      const live = new Set<number>();
      for (const g of msg.grenades) {
        live.add(g.id);
        if (!this.seenGrenades.has(g.id)) {
          this.players.get(g.ownerSessionId)?.playThrow();
        }
      }
      this.seenGrenades = live;
    });

    this.app.on("update", (dt: number) => {
      // One render-timeline sample per frame, shared by every remote body.
      const renderTick = this.client.getRemoteRenderTick();
      for (const p of this.players.values()) p.update(dt, renderTick);
    });
  }

  private onAdd(player: RemotePlayerState, sessionId: string): void {
    if (sessionId === this.client.sessionId) return; // skip self
    if (this.players.has(sessionId)) return;

    const team = player.team ?? "ct";
    const accent =
      team === "ct"
        ? new pc.Color(0.28, 0.58, 0.96)
        : new pc.Color(0.95, 0.42, 0.18);

    const remote = new RemotePlayer(this.app, sessionId, player, accent, this.characters);
    this.players.set(sessionId, remote);
    console.log(`[RemotePlayerManager] spawned remote ${sessionId} (${team})`);
  }

  private onChange(player: RemotePlayerState, sessionId: string): void {
    if (sessionId === this.client.sessionId) return; // local handled by FirstPersonController
    const remote = this.players.get(sessionId);
    if (!remote) return;
    // Halftime side swap — team is baked into the body at construction
    // (accent tint + CT/T placeholder gear + character material), so a
    // snapshot arriving on the other side means rebuild the body fresh.
    if (player.team && player.team !== remote.team) {
      this.onRemove(sessionId);
      this.onAdd(player, sessionId);
      return;
    }
    remote.setTarget(player);
  }

  private onRemove(sessionId: string): void {
    const remote = this.players.get(sessionId);
    if (!remote) return;
    remote.destroy();
    this.players.delete(sessionId);
    console.log(`[RemotePlayerManager] despawned remote ${sessionId}`);
  }

  /**
   * World position of a remote player's hand/weapon, for peer shot +
   * tracer origins. Returns false for unknown / dead players so the
   * caller can fall back to its eye-relative heuristic.
   */
  getHandWorld(sessionId: string, out: pc.Vec3): boolean {
    return this.players.get(sessionId)?.getHandWorld(out) ?? false;
  }
}
